import type { Operation } from '@mystiq/contracts';
import { useRef, useState } from 'react';
import { useFolders, useWorkspaceMutation } from '../api/hooks.ts';
import { useApi } from '../auth/session.tsx';
import { folderPaths } from './MoveDialog.tsx';
import { ErrorLine, Modal } from './ui.tsx';

type SiteRequest = { sourceItemIds: string[]; instructions?: string; title?: string; folderId?: string };

/**
 * Asks AI to make an interactive Site from the chosen items. The Site is an
 * item of its own: it stays when the sources change or go to Trash, and it
 * runs separately from Mystiq, without internet access.
 */
export function SiteDialog({ ws, sourceItemIds, onClose, onRequested }: { ws: string; sourceItemIds: string[]; onClose(): void; onRequested(operation: Operation): void }) {
  const api = useApi();
  const folders = useFolders(ws);
  const [instructions, setInstructions] = useState('');
  const [title, setTitle] = useState('');
  const [folderId, setFolderId] = useState('');
  const make = useWorkspaceMutation(ws, (input: SiteRequest & { idempotencyKey: string }) => api.createSite(ws, input));
  // Asking again with the same choices (say, after a lost response) repeats the same request, never a second paid run.
  const lastRequest = useRef<{ payload: string; key: string } | null>(null);
  const count = sourceItemIds.length;

  return (
    <Modal title={count > 1 ? `Make a Site from ${count} items` : 'Make a Site'} onClose={onClose}>
      <p className="meta">AI turns the text into one interactive page, such as a quiz, flashcards, a calculator or a guide.</p>
      <label className="field">
        <span className="field-label">What should it be? (optional)</span>
        <textarea
          className="textarea textarea-short"
          maxLength={4000}
          value={instructions}
          placeholder="For example: a quiz with ten questions and a score"
          onChange={(event) => setInstructions(event.target.value)}
        />
      </label>
      <label className="field">
        <span className="field-label">Title (optional)</span>
        <input className="input" value={title} maxLength={200} placeholder="AI suggests one" onChange={(event) => setTitle(event.target.value)} />
      </label>
      <label className="field">
        <span className="field-label">Save to</span>
        <select className="select" value={folderId} onChange={(event) => setFolderId(event.target.value)}>
          <option value="">{count > 1 ? 'Same folder as the items (or Inbox)' : 'Same folder'}</option>
          {folderPaths(folders.data ?? []).map((folder) => (
            <option key={folder.id} value={folder.id}>
              {folder.path}
            </option>
          ))}
        </select>
      </label>
      <p className="meta">The Site is saved as an item of its own. It runs separately from Mystiq and cannot reach your workspace or the internet.</p>
      <ErrorLine error={make.error} />
      <div className="modal-actions">
        <button
          className="button button-primary"
          disabled={make.isPending}
          onClick={async () => {
            const request: SiteRequest = {
              sourceItemIds,
              ...(instructions.trim() ? { instructions: instructions.trim() } : {}),
              ...(title.trim() ? { title: title.trim() } : {}),
              ...(folderId ? { folderId } : {}),
            };
            const payload = JSON.stringify(request);
            if (lastRequest.current?.payload !== payload) lastRequest.current = { payload, key: crypto.randomUUID() };
            const operation = await make.mutateAsync({ ...request, idempotencyKey: lastRequest.current.key }).catch(() => null);
            if (operation) {
              onRequested(operation);
              onClose();
            }
          }}
        >
          Make Site
        </button>
        <button className="button" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Modal>
  );
}
